/* eslint-disable no-nested-ternary */
import React, { useState, useMemo } from "react";

import { Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Skeleton } from "@mui/material";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../context/StateContext";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

type LatLng = {
  lat: number;
  lng: number;
};

export type VoyageMapInformationProps = {
  vesselName?: string;
  position?: LatLng;
  lastUpdate?: Date | string;
  speed?: string;
};

type VoyageMapProps = {
  information?: VoyageMapInformationProps;
  loadingMap?: boolean;
  showLivePosition?: boolean;
  externalMapIsLoaded?: boolean;
  isAirTracking?: boolean;
  isCustomerPortal?: boolean;
};

const DATE_FORMAT = "dd/MM/yyyy hh:mm aa";

const PLANE_ICON = require("../../../assets/img/plane_icon.svg");
const MARKER_CUSTOMER_PORTAL_ICON = require("../../../assets/img/ship-marker-2.svg");
const MARKER_ICON = require("../../../assets/img/ship-marker.svg");

/**
 * Component to render a map with marker, showing the informations (vessel name, latitude, longitude) when click on marker
 * @param information  - Vessel name and position of Latitude and Longitude to  set map position of center and show on modal when click on marker
 * @returns Map JSX Element with voyage props
 */
export const VoyageMap: React.FC<VoyageMapProps> = ({
  information,
  loadingMap = true,
  showLivePosition = true,
  children,
  externalMapIsLoaded,
  isAirTracking,
  isCustomerPortal,
}) => {
  const classes = useStyles();

  const { mapIsLoaded } = useAppState();

  const [zoomMap] = useState(3);

  const [informationWindowOpened, setInformationWindowOpened] = useState(false);

  const showMap = useMemo(
    () => !loadingMap && (mapIsLoaded || externalMapIsLoaded),
    [loadingMap, mapIsLoaded, externalMapIsLoaded]
  );

  const getIconSize = () => {
    return isAirTracking ? new google.maps.Size(27, 32) : new google.maps.Size(20, 20);
  };

  const getIconUri = () => {
    if (isCustomerPortal) return MARKER_CUSTOMER_PORTAL_ICON;
    if (isAirTracking) return PLANE_ICON;

    return MARKER_ICON;
  };

  const renderMap = () => {
    if (information && information.position && showMap) {
      return (
        <div className={classes.map}>
          <GoogleMap
            center={information.position}
            zoom={zoomMap}
            options={{
              minZoom: 2,
              maxZoom: 15,
            }}
            mapContainerClassName={classes.map}
          >
            {informationWindowOpened && information && (
              <InfoWindow
                position={information.position}
                onCloseClick={() => setInformationWindowOpened(false)}
              >
                <InfoWindowContent data={information} isAirTracking={isAirTracking} />
              </InfoWindow>
            )}
            {showLivePosition ? (
              <Marker
                icon={{
                  url: getIconUri(),
                  anchor: new google.maps.Point(1, 5),
                  scaledSize: getIconSize(),
                }}
                position={information.position}
                onClick={() => setInformationWindowOpened(true)}
                onMouseOver={isAirTracking ? () => setInformationWindowOpened(true) : () => {}}
              ></Marker>
            ) : null}
            {children}
          </GoogleMap>
        </div>
      );
    }
  };

  return (
    <Grid container direction="column">
      {!showMap && <Skeleton className={classes.skeleton} />}
      {renderMap()}
    </Grid>
  );
};

type InfoWindowContentProps = {
  data: VoyageMapInformationProps;
  isAirTracking?: boolean;
};

const InfoWindowContent = ({ data, isAirTracking }: InfoWindowContentProps) => {
  const { vesselName, position, lastUpdate, speed } = data;
  const { t } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const date = lastUpdate
    ? lastUpdate instanceof Date
      ? formatDateWithUserPref(lastUpdate, currentUser!.preferences!)
      : formatDateWithUserPref(new Date(lastUpdate), currentUser!.preferences!)
    : "";

  const speedConverted = useMemo(
    () => (speed && !Number.isNaN(Number(speed)) ? Number(speed) * 10 : ""),
    [speed]
  );

  return (
    <Grid container direction="column" className={classes.windowContainer}>
      <Grid container direction="row" alignItems="center" className={classes.windowContent}>
        <Grid item className={classes.title}>
          {isAirTracking ? t("flightNumber") : t("vesselName")}:{" "}
        </Grid>
        <Grid item className={classes.description}>
          {vesselName}
        </Grid>
      </Grid>
      <Grid container direction="row" alignItems="center" className={classes.windowContent}>
        <Grid item className={classes.title}>
          {t("latitude")}:{" "}
        </Grid>
        <Grid item className={classes.description}>
          {position?.lat}
        </Grid>
      </Grid>
      <Grid container direction="row" alignItems="center" className={classes.windowContent}>
        <Grid item className={classes.title}>
          {t("longitude")}:{" "}
        </Grid>
        <Grid item className={classes.description}>
          {position?.lng}
        </Grid>
      </Grid>
      {speedConverted && (
        <Grid container direction="row" alignItems="center" className={classes.windowContent}>
          <Grid item className={classes.title}>
            {t("speedKnots")}:{" "}
          </Grid>
          <Grid item className={classes.description}>
            {speedConverted}
          </Grid>
        </Grid>
      )}
      {date && (
        <Grid container direction="row" alignItems="center" className={classes.windowContent}>
          <Grid item className={classes.title}>
            {t("lastUpdate")}:{" "}
          </Grid>
          <Grid item className={classes.description}>
            {date} (UTC)
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const useStyles = makeStyles(() => ({
  skeleton: {
    transform: "none",
    minHeight: "276px",
    minWidth: "300px",
    width: "100%",
    height: "100%",
    marginTop: "12px",
  },
  map: {
    minHeight: "276px",
    minWidth: "100%",
    width: "100%",
    "& div": {
      borderRadius: "8px !important",
    },
    "& .gm-svpc": {
      display: "none",
    },
    "& .gm-style-mtc": {
      display: "none",
    },
    "& .gm-fullscreen-control": {
      display: "none",
    },
  },
  mapTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#465869",
    marginBottom: "24px",
  },
  windowContainer: {
    padding: "5px 5px 0px 5px",
  },
  windowContent: {
    marginBottom: "5px",
  },
  title: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#9CA6AF",
    whiteSpace: "nowrap",
    marginRight: "5px",
    fontFamily: "Manrope !important",
  },
  description: {
    fontSize: "12px",
    color: "#465869",
    fontFamily: "Manrope !important",
  },
}));
